'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["showOpacityInput", "showInput"],
  _excluded2 = ["disabled"],
  _excluded3 = ["className", "color", "colorInputLabel", "ColorPicker", "colorValidator", "disabled", "id", "inputClassName", "inputProps", "onColorChange", "onOpacityChange", "onOpenChange", "opacity", "opacityInputClassName", "opacityInputLabel", "opacityInputProps", "open", "readOnly", "showInput", "swatchClassName", "swatchProps", "use"];
import PropTypes from 'prop-types';
import { Component } from 'react';
import styled from 'styled-components';
import UIInputStaticLabel from '../input/UIInputStaticLabel';
import UITextInput from '../input/UITextInput';
import UIStepperInput from '../input/UIStepperInput';
import UIColorSwatch from './UIColorSwatch';
import * as CustomRenderer from '../utils/propTypes/customRenderer';
import passthroughProps from '../utils/propTypes/passthroughProps';
import * as Color from '../utils/propTypes/color';
import Controllable from '../decorators/Controllable';
import SyntheticEvent from '../core/SyntheticEvent';
import memoizeOne from 'react-utils/memoizeOne';
import { FORM_CONTROL_BORDER_COLOR } from 'HubStyleTokens/theme';
import { OLAF } from 'HubStyleTokens/colors';
import ColorPickerShim from './ColorPickerShim';
import UIPopover from '../tooltip/UIPopover';
import UIFormControl from '../form/UIFormControl';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_COLOR = '';
const COLOR_INPUT_ALIGNMENT = 'top';
const isEmptyState = color => color === DEFAULT_COLOR || color === null || color === '#';
const prependHexChar = color => color[0] === '#' ? color : `#${color}`;
const getDisplayColor = color => (color || '').replace('#', '');
const getSwatchColor = (color, lastColor = OLAF) => {
  if (isEmptyState(color)) return OLAF;
  if (color.length > 4 && color.length < 7) {
    return color.slice(0, 4);
  }
  return color.length <= 3 ? lastColor : color;
};
const NoopColorPicker = () => /*#__PURE__*/_jsx("span", {});
const InputWrapper = styled.div.withConfig({
  displayName: "UIColorInput__InputWrapper",
  componentId: "sc-1k1l86m-0"
})(["display:inline-block;"]);
const FormControl = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(UIFormControl, Object.assign({}, rest));
}).withConfig({
  displayName: "UIColorInput__FormControl",
  componentId: "sc-1k1l86m-1"
})(["display:inline-block;vertical-align:", ";width:115px;&&{", "}"], COLOR_INPUT_ALIGNMENT, ({
  showOpacityInput
}) => showOpacityInput && `display: inline-block;
      width: 95px;
      margin-left: 8px;
      margin-top: 0px !important;
    `);
const SwatchWrapper = styled.div.withConfig({
  displayName: "UIColorInput__SwatchWrapper",
  componentId: "sc-1k1l86m-2"
})(["display:inline-block;margin-left:", ";vertical-align:", ";"], ({
  showInput
}) => showInput ? '8px' : '0px', COLOR_INPUT_ALIGNMENT);
const ColorSwatch = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded2);
  return /*#__PURE__*/_jsx(UIColorSwatch, Object.assign({}, rest));
}).withConfig({
  displayName: "UIColorInput__ColorSwatch",
  componentId: "sc-1k1l86m-3"
})(["cursor:", ";"], ({
  disabled
}) => disabled ? 'not-allowed' : 'default');
const ColorPickerWrapper = styled.div.withConfig({
  displayName: "UIColorInput__ColorPickerWrapper",
  componentId: "sc-1k1l86m-4"
})(["box-shadow:none;border:0;"]);
const renderColorPickerPanel = (computedColorPicker, open, swatchColor, opacity, handleColorChange, handleOpenChange, popoverTarget) => {
  if (!open && computedColorPicker !== 'native' || computedColorPicker === NoopColorPicker) return null;
  const renderedContent = CustomRenderer.render(computedColorPicker === 'native' ? ColorPickerShim : computedColorPicker, {
    open,
    hexOnly: false,
    color: swatchColor,
    alpha: opacity,
    onChange: handleColorChange,
    mode: 'RGB',
    includeAlpha: Boolean(opacity)
  });
  return computedColorPicker === 'native' ? renderedContent : /*#__PURE__*/_jsx(UIPopover, {
    autoPlacement: "horiz",
    closeOnOutsideClick: true,
    content: renderedContent,
    onOpenChange: handleOpenChange,
    open: open,
    placement: "right",
    target: popoverTarget
  });
};
class UIColorInput extends Component {
  static getDerivedStateFromProps(props, state) {
    const {
      prevColor
    } = state;
    const {
      color
    } = props;
    if (color !== prevColor) {
      return {
        prevColor: color,
        swatchColor: getSwatchColor(color, state.swatchColor)
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.handleColorChange = evt => {
      const {
        onColorChange,
        onOpacityChange,
        opacity
      } = this.props;
      if (opacity && evt.alpha) {
        onOpacityChange(evt.target ? evt : SyntheticEvent(evt.alpha));
      }
      onColorChange(evt.target ? evt : SyntheticEvent(evt.color));
    };
    this.handleInputChange = ({
      target: {
        value
      }
    }) => {
      const {
        colorValidator,
        readOnly
      } = this.props;
      if (readOnly) return;
      if (colorValidator(value)) {
        if (!value) {
          this.handleColorChange(SyntheticEvent(null));
        } else {
          this.handleColorChange(SyntheticEvent(prependHexChar(value)));
        }
      }
    };
    this.handleOpacityInputChange = evt => {
      const {
        onOpacityChange,
        readOnly
      } = this.props;
      if (readOnly) return;
      onOpacityChange(evt);
    };
    this.handleOpenChange = () => {
      const {
        onOpenChange,
        open
      } = this.props;
      onOpenChange(SyntheticEvent(!open));
    };
    this.refCallback = elt => {
      if (!elt) return;
      this._triggerEl = elt;
      this.setState({
        targetLoaded: true
      });
    };
    this.state = {
      swatchColor: getSwatchColor(this.props.color),
      targetLoaded: false
    };
    this._renderColorPickerPanel = memoizeOne(renderColorPickerPanel);
  }
  render() {
    const _this$props = this.props,
      {
        className,
        color,
        colorInputLabel,
        ColorPicker,
        disabled,
        id,
        inputClassName,
        inputProps,
        opacity,
        opacityInputClassName,
        opacityInputLabel,
        opacityInputProps,
        open,
        readOnly,
        showInput,
        swatchClassName,
        swatchProps,
        use
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded3);
    const {
      swatchColor
    } = this.state;
    const computedColorPicker = readOnly || disabled ? NoopColorPicker : ColorPicker;
    const renderedHexInput = disabled ? /*#__PURE__*/_jsx(UITextInput, Object.assign({
      className: inputClassName,
      id: id,
      disabled: true,
      use: use,
      value: `#${getDisplayColor(color)}`
    }, inputProps)) : /*#__PURE__*/_jsx(UIInputStaticLabel, {
      text: "#",
      children: /*#__PURE__*/_jsx(UITextInput, Object.assign({
        className: inputClassName,
        id: id,
        maxLength: 7,
        onChange: this.handleInputChange,
        spellCheck: false,
        use: use,
        value: getDisplayColor(color),
        readOnly: readOnly
      }, inputProps))
    });
    const showOpacityInput = typeof opacity !== 'undefined' && showInput;
    const renderedOpacityInput = showOpacityInput ? /*#__PURE__*/_jsx(FormControl, {
      "aria-label": opacityInputLabel || 'opacity input',
      label: opacityInputLabel,
      showOpacityInput: showOpacityInput,
      children: /*#__PURE__*/_jsx(UIStepperInput, Object.assign({
        className: opacityInputClassName,
        disabled: disabled,
        value: opacity,
        onChange: this.handleOpacityInputChange,
        min: 0,
        max: 100,
        formatStyle: "percentage",
        readOnly: readOnly,
        use: use
      }, opacityInputProps))
    }) : null;
    const renderedColorPickerPanel = this._renderColorPickerPanel(computedColorPicker, open, swatchColor, opacity, this.handleColorChange, this.handleOpenChange, this._triggerEl);
    return /*#__PURE__*/_jsx(InputWrapper, Object.assign({}, rest, {
      className: className,
      children: /*#__PURE__*/_jsxs("div", {
        ref: this.refCallback,
        children: [/*#__PURE__*/_jsx(FormControl, {
          "aria-label": colorInputLabel || 'color input',
          label: colorInputLabel,
          showInput: showInput,
          className: !showInput ? 'hidden' : '',
          children: renderedHexInput
        }), renderedOpacityInput, /*#__PURE__*/_jsxs(SwatchWrapper, {
          showInput: showInput,
          children: [/*#__PURE__*/_jsx(ColorSwatch, Object.assign({
            className: swatchClassName,
            borderColor: FORM_CONTROL_BORDER_COLOR,
            borderWidth: 1
          }, swatchProps, {
            opacity: opacity,
            color: swatchColor,
            onClick: disabled || readOnly ? null : this.handleOpenChange,
            readOnly: disabled || readOnly,
            disabled: disabled
          })), /*#__PURE__*/_jsx(ColorPickerWrapper, {
            children: renderedColorPickerPanel
          })]
        })]
      })
    }));
  }
}
UIColorInput.displayName = 'UIColorInput';
UIColorInput.defaultProps = {
  color: DEFAULT_COLOR,
  colorValidator: Color.isValidColorHex,
  open: false,
  ColorPicker: NoopColorPicker,
  readOnly: false,
  showInput: true,
  use: UITextInput.defaultProps.use
};
UIColorInput.propTypes = {
  ColorPicker: PropTypes.oneOfType([CustomRenderer.propType, PropTypes.oneOf(['native'])]).isRequired,
  color: Color.propType,
  colorValidator: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  inputClassName: PropTypes.string,
  inputProps: PropTypes.object,
  onColorChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  readOnly: PropTypes.bool,
  showInput: PropTypes.bool,
  swatchClassName: PropTypes.string,
  swatchProps: passthroughProps(UIColorSwatch),
  opacity: PropTypes.number,
  onOpacityChange: PropTypes.func,
  colorInputLabel: PropTypes.string,
  opacityInputClassName: PropTypes.string,
  opacityInputLabel: PropTypes.string,
  opacityInputProps: PropTypes.object,
  use: UITextInput.propTypes.use
};
export default Controllable(UIColorInput, ['color', 'open', 'opacity']);